// Datos estratégicos por indicador — espejo cliente del Motor Estratégico (backend).
// Lenguaje probabilístico/pedagógico, NO determinista.
// Actualizado a las rúbricas oficiales 2026 (CPEIP / DocenteMás).
//
// 🆕 CAMBIOS 2026:
// - Eliminado el indicador 1.8 ("Uso del error como oportunidad").
// - Eliminados del Módulo 3: 3.17 (Reflexión necesidades profesionales),
//   3.19 (Seguimiento de la implementación), y la versión "Reflexión sobre creencias pedagógicas".
// - Tarea 2 reformulada (1.4, 1.5, 1.6) con enfoque de MONITOREO de los aprendizajes.
//
// Cada indicador tiene:
//   - tipo:       'Tractor' | 'Focalizado' | 'Mantención' | 'Alto Desgaste'
//   - cluster:    id del cluster pedagógico
//   - arrastre:   1-5 (capacidad de arrastrar otros indicadores)
//   - impacto:    1-5 (impacto en resultado global)
//   - arrastra:   array opcional de indicadores asociados (cuando es Tractor)

export const CLUSTERS_NOMBRES = {
    outlier: 'Outlier — Formulación de objetivos',
    planificacion_tecnica: 'Planificación Técnica / Monitoreo',
    practica_aula: 'Práctica de Aula (alto desgaste)',
    reflexion_participacion: 'Reflexión y Participación',
};

export const MOTOR_INDICADOR_META = {
    '1.1': { nombre: 'Formulación de objetivos', tipo: 'Tractor', cluster: 'outlier', arrastre: 5, impacto: 5 },
    '1.2': { nombre: 'Relación actividades–objetivos', tipo: 'Tractor', cluster: 'reflexion_participacion', arrastre: 5, impacto: 4,
             arrastra: ['Fundamentación', 'Estrategia de monitoreo', 'Análisis del monitoreo', 'Uso formativo del monitoreo', 'Contribución períodos', 'Uso de conocimientos previos'] },
    '1.3': { nombre: 'Fundamentación de la planificación', tipo: 'Mantención', cluster: 'planificacion_tecnica', arrastre: 3, impacto: 3 },
    '1.4': { nombre: 'Estrategia de monitoreo de los aprendizajes', tipo: 'Mantención', cluster: 'planificacion_tecnica', arrastre: 2, impacto: 3 },
    '1.5': { nombre: 'Análisis a partir del monitoreo', tipo: 'Mantención', cluster: 'planificacion_tecnica', arrastre: 2, impacto: 3 },
    '1.6': { nombre: 'Uso formativo del monitoreo', tipo: 'Focalizado', cluster: 'planificacion_tecnica', arrastre: 4, impacto: 4 },
    '1.7': { nombre: 'Reflexión socioemocional', tipo: 'Tractor', cluster: 'reflexion_participacion', arrastre: 5, impacto: 5,
             arrastra: ['Participación', 'Contribución períodos', 'Aprendizajes profesionales', 'Evaluación conjunta de la forma de trabajar'] },

    '2.9':  { nombre: 'Ambiente para el aprendizaje', tipo: 'Alto Desgaste', cluster: 'practica_aula', arrastre: 4, impacto: 4 },
    '2.10': { nombre: 'Promoción de participación y colaboración', tipo: 'Tractor', cluster: 'reflexion_participacion', arrastre: 4, impacto: 4,
             arrastra: ['Reflexión socioemocional', 'Ambiente', 'Habilidades de aprendizaje profundo', 'Uso de conocimientos', 'Uso de desempeños'] },
    '2.11': { nombre: 'Contribución de períodos de la jornada', tipo: 'Focalizado', cluster: 'reflexion_participacion', arrastre: 3, impacto: 3 },
    '2.12': { nombre: 'Habilidades para aprendizaje profundo', tipo: 'Alto Desgaste', cluster: 'practica_aula', arrastre: 3, impacto: 4 },
    '2.13': { nombre: 'Uso de conocimientos y experiencias', tipo: 'Focalizado', cluster: 'reflexion_participacion', arrastre: 4, impacto: 4 },
    '2.14': { nombre: 'Uso de desempeños estudiantiles', tipo: 'Alto Desgaste', cluster: 'practica_aula', arrastre: 3, impacto: 3 },
    '2.15': { nombre: 'Equidad de género', tipo: 'Alto Desgaste', cluster: 'practica_aula', arrastre: 3, impacto: 3 },

    '3.16': { nombre: 'Relevancia del problema a partir del análisis de evidencia', tipo: 'Focalizado', cluster: 'reflexion_participacion', arrastre: 3, impacto: 4 },
    '3.18': { nombre: 'Diálogo pedagógico en torno a la implementación', tipo: 'Alto Desgaste', cluster: 'practica_aula', arrastre: 4, impacto: 4 },
    '3.20': { nombre: 'Aprendizajes profesionales a partir de la experiencia colaborativa', tipo: 'Tractor', cluster: 'reflexion_participacion', arrastre: 4, impacto: 5,
             arrastra: ['Relevancia del problema', 'Diálogo pedagógico', 'Evaluación conjunta de la forma de trabajar'] },
    '3.22': { nombre: 'Evaluación conjunta de la forma de trabajar', tipo: 'Focalizado', cluster: 'planificacion_tecnica', arrastre: 3, impacto: 3 },
};

// Mapeo desde el indicador_id largo (formato app) al ID corto del motor.
// Solo se incluyen indicadores VIGENTES en 2026.
const _LARGO_A_CORTO = {
    'indicador-1-1': '1.1',
    'indicador-1-2': '1.2',
    'indicador-1-3': '1.3',
    'indicador-2-1': '1.4',
    'indicador-2-2': '1.5',
    'indicador-2-3': '1.6',
    'indicador-3-1': '1.7',
    'indicador-m2-9': '2.9',
    'indicador-m2-10': '2.10',
    'indicador-m2-11': '2.11',
    'indicador-m2-12': '2.12',
    'indicador-m2-13': '2.13',
    'indicador-m2-14': '2.14',
    'indicador-m2-15': '2.15',
    'indicador-m3-16': '3.16',
    'indicador-m3-18': '3.18',
    'indicador-m3-21': '3.20',
    'indicador-m3-23': '3.22',
};

export function motorMetaForIndicador(indicadorId) {
    if (!indicadorId) return null;
    const corto = _LARGO_A_CORTO[indicadorId];
    if (!corto) return null;
    const meta = MOTOR_INDICADOR_META[corto];
    if (!meta) return null;
    return { ...meta, id: corto };
}

/**
 * Devuelve el nivel de impacto estratégico de un indicador:
 *   - 'tractor'  → tipo === Tractor
 *   - 'alto'     → impacto >= 4 o tipo Focalizado con arrastre >= 4
 *   - 'medio'    → impacto === 3 o (Focalizado con arrastre 3)
 *   - 'bajo'     → resto (Mantención, Alto Desgaste con bajo arrastre)
 * El banner se muestra solo para nivel 'alto' o 'tractor' por defecto.
 */
export function impactoEstrategico(meta) {
    if (!meta) return null;
    if (meta.tipo === 'Tractor') return 'tractor';
    if (meta.impacto >= 4 || (meta.tipo === 'Focalizado' && meta.arrastre >= 4)) return 'alto';
    if (meta.impacto === 3 || meta.arrastre === 3) return 'medio';
    return 'bajo';
}
